import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';

/**
 * Angular version of cl-button from AngularJS side
 * This is duplicated rather than replaced in order to 'phase-out' the old button as is convenient,
 * as converting it all at once would be a huge job -- lots of icons that would have to be re-registered
 */

@Component({
    encapsulation: ViewEncapsulation.None, // there are styles for this in normal external stylesheets
    selector: 'cas-button',
    styleUrls: ['./casButton.scss'],
    template: `<button mat-button
                       [class.mat-icon-button]="iconButton"
                       [class.mat-flat-button]="!iconButton"
                       [attr.type]="btnType"
                       [ngClass]="btnClass"
                       [autofocus]="autofocus"
                       (click)="executeAction($event)"
                       [color]="color"
                       [disabled]="disabledByAction || disabled">
                   <mat-icon *ngIf="icon" [svgIcon]="icon" [ngClass]="iconClass"></mat-icon>
                   <mat-icon *ngIf="materialIcon" [ngClass]="iconClass">{{materialIcon}}</mat-icon>
                   <span *ngIf="!iconButton" [textContent]="btnText"></span>
               </button>`,
})
export class CasButtonComponent {
    public static $name: string = 'casButton';

    /** If this is true, the button will be autofocused, so that, for example, in a dialog, pressing 'Enter' will activate that button */
    @Input() public autofocus: boolean = false;

    /**
     * Pass in ng-click behavior as click-action so that it is applied to the mat-button and not the cl-button --
     * this is to prevent issues with disabled buttons being able to still perform their behavior
     */
    @Output() public clickAction: EventEmitter<any> = new EventEmitter(true);

    @Input() public color: 'primary' | 'accent' | 'warn' | null = 'accent';

    /** If this is true, the button will be disabled. Kept internal to ClButtonController */
    @Input() public disabledByAction: boolean = false;

    /** If this is true, the button will be disabled. This one is evaluated from a passed-in expression */
    @Input() public disabled: boolean = false;

    /**
     * If this is true, the btnText will be applied to the aria-label but not the span,
     * and the mat-icon-button class will be applied to the outer mat-button
     */
    @Input() public iconButton: boolean = false;

    /** Any classes that exist here will be applied to the outer mat-button */
    @Input() public btnClass: string;

    @Input() public btnType: string = 'button';

    /** The mat-icon identifier, anything passed in here must be registered with $mdIconProvider */
    @Input() public icon: string;
    @Input() public materialIcon: string;

    /** Any classes that exist here will be applied to the inner mat-icon */
    @Input() public iconClass: string;

    /** Any string passed in here will be applied to both the span and the aria-label, UNLESS iconButton is true */
    @Input() public btnText: string;

    /** If this is passed in, the ng-click event will stop propagation */
    @Input() public stopPropagation: boolean = true;

    /** If you pass a tooltip direction in, the component will use the btnText passed in to generate a tooltip */
    @Input() public tooltipDirection: string;

    public executeAction($event: Event) {
        if (this.stopPropagation) {
            $event.stopPropagation();
        }

        this.disabledByAction = true;

        Promise
            .resolve(this.clickAction.emit($event))
            .finally(() => {
                this.disabledByAction = false;
            });
    }
}
